import React, { useState } from 'react';
import { IconButton, InputAdornment, Alert, AlertTitle, Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import axios from 'axios';
import { CustomButton, CustomTextField } from './CustomComponents';
import urbanImage from '../assets/urban_logo.png';
import { emailMessage, requiredMessage } from '../interfaces/Forms';
import { useUser } from '../context/UserContext';
import type { AuthResponse } from '../types/user';
import { ADMIN_HOME_PATH, HOME_PATH } from '../routes/routePaths';

interface LoginForm {
  email: string;
  password: string;
}

function Login(): React.ReactElement {
  const navigate = useNavigate();
  const { setAuth } = useUser();
  const isAdmin = useLocation().state?.isAdmin || false;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | null>(null);

  const initialValues: LoginForm = {
    email: useLocation().state?.email || '',
    password: '',
  };

  const loginSchema = object({
    email: string().required(requiredMessage).email(emailMessage),
    password: string().required('Password is required'),
  });

  const formik = useFormik<LoginForm>({
    initialValues,
    enableReinitialize: true,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoginError(false);
      setLoginErrorMessage(null);
      try {
        const { data } = await axios.post<AuthResponse>(`${process.env.REACT_APP_API_URL}/login`, {
          email: values.email,
          password: values.password,
        });
        if (data?.data?.token && data?.data?.user) {
          const authUser = data.data.user;
          setAuth(data.data.token, authUser);
          navigate(authUser.isAdmin ? ADMIN_HOME_PATH : HOME_PATH);
        } else {
          setLoginError(true);
        }
      } catch (err) {
        setLoginError(true);
        if (axios.isAxiosError(err) && err.response?.data?.message) {
          setLoginErrorMessage(String(err.response.data.message));
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '40px 20px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: '450px',
          maxWidth: '600px',
          padding: '0 40px 40px 40px',
          borderRadius: 4,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <img
            src={urbanImage}
            alt="Urban Utility Logo"
            style={{
              width: '320px',
              objectFit: 'contain',
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Bienvenido de vuelta {isAdmin ? 'Administrador' : ''}
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 2, fontSize: '14px' }}>
          Por favor ingresa tus datos para iniciar sesión
        </Typography>
        <form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
          <CustomTextField
            fullWidth
            required
            id="email"
            name="email"
            label="Email"
            placeholder="nombre@email.com"
            value={formik.values.email}
            variant="outlined"
            type="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('email')}
            error={(formik.touched.email && Boolean(formik.errors.email)) || loginError}
            helperText={formik.touched.email && formik.errors.email}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ pb: '20px' }}
          />
          <Typography
            variant="body2"
            textAlign="right"
            sx={{
              position: 'relative',
              zIndex: 10,
              mb: -2,
              '& a': {
                textDecoration: 'none',
                color: '#484855',
                fontWeight: 500,
              },
              '& a:hover': {
                textDecoration: 'underline',
                color: '#000',
              },
            }}
          >
            <a href="/recover-password">¿Olvidaste tu contraseña?</a>
          </Typography>
          <CustomTextField
            fullWidth
            required
            id="password"
            name="password"
            label="Contraseña"
            placeholder="••••••••••"
            value={formik.values.password}
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur('password')}
            error={(formik.touched.password && Boolean(formik.errors.password)) || loginError}
            helperText={formik.touched.password && formik.errors.password}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <CustomButton
            fullWidth
            type="submit"
            variant="contained"
            disableElevation
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </CustomButton>
          {loginError && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                justifyContent: 'center',
                '& .MuiAlert-icon': {
                  marginRight: -2,
                },
              }}
            >
              <AlertTitle>Error</AlertTitle>
              {loginErrorMessage || 'Credenciales Incorrectas'}
            </Alert>
          )}
        </form>
        {!isAdmin && (
          <Typography
            variant="body2"
            sx={{
              pt: '20px',
              '& a': {
                textDecoration: 'none',
                color: 'black',
                fontWeight: 600,
              },
              '& a:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Nuevo consignante? &nbsp;<a href="/create-account">Crear cuenta</a>
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="textSecondary" sx={{ mt: '10px' }}>
        © {new Date().getFullYear()} URBAN UTILITY
      </Typography>
    </Box>
  );
}

export default Login;
